import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { BackHeader } from '../../components/BackHeader';
import DatePicker from '../../components/DatePicker';
import { api, apiClient, openUploadDownload } from '../../api/client';
import { putToPresignedUrl } from '../../api/upload';
import { cleanFormPayload } from '../../lib/cleanFormPayload';
import {
  Settings, Calendar, Shield, Download, FileText, Loader2,
  MapPin, ArrowRightLeft, ExternalLink, Upload, X, Pencil, Trash2,
  Wrench, Plus,
  Camera, RefreshCw, ClipboardCheck,
} from 'lucide-react';
import MaintenanceRecordDialog, { fmtDate, type MaintenanceRecord } from '../../components/maintenance/MaintenanceRecordDialog';
import InspectionCadenceLine from '../../components/equipment/InspectionCadenceLine';
import { canWriteServicePolicies } from '../../lib/service-policies';
import { MaintenanceSchedules } from '../../components/maintenance/MaintenanceSchedules';
import { AssetDocuments } from '../../components/equipment/AssetDocuments';
import { AssetExpenses } from '../../components/equipment/AssetExpenses';
import { VerifyCheckOutButton } from '../../components/equipment/VerifyCheckOutButton';
import { Tooltip } from '../../components/ui/tooltip';
import type { CurrentMeter, CurrentMeters } from '../../types/equipment';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { calendarDayString, formatCalendarDate, formatInstantTenantDay } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { fmtCurrency, sumMoney } from '../../lib/display-labels';
import { CERT_LEVEL_PRESETS, certLevelLabel, isPresetDayLimit } from '../../lib/equipmentCertLevel';
import { CertDayCounter } from '../../components/CertDayCounter';
import { MaintenanceFlagsPanel } from '../../components/equipment/MaintenanceFlagsPanel';
import { PressureControlPanel } from '../../components/equipment/PressureControlPanel';
import { MAINTENANCE_FLAGS_ROOT } from '../../hooks/useMaintenanceFlags';
import { certDayPercent, certDayTextColor, formatCertDays } from '../../lib/cert-days';
import { canManageEquipmentCertDocuments, canWriteCertFields } from '../../lib/roles';
import {
  CertAdjustmentEntryPanel,
  CertCorrectionDialog,
  useCertAdjustments,
  certAdjustmentLabel,
  type CertAdjustmentRecord,
} from '../../components/equipment/CertAdjustments';
import { CertLedger } from '../../components/equipment/CertLedger';
import { EquipmentHistoryCard, LastJobLine } from '../../components/equipment/EquipmentHistory';
import { parseLastJob } from '../../lib/equipment-history';

type VehicleTab = 'overview' | 'documents' | 'maintenance' | 'expenses' | 'photos';
const VEHICLE_TABS: VehicleTab[] = ['overview', 'documents', 'maintenance', 'expenses', 'photos'];

const inputClass = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-amber-500/50 transition-colors';
const labelClass = 'block text-xs text-fieldiq-text mb-1.5 font-medium';

export const EquipmentDetail: React.FC = () => {
  const { equipmentId } = useParams<{ equipmentId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Equipment inspections are a casing-running product. Tenants without that
  // vertical have no inspection templates behind the button. `user` is read
  // here rather than lower down because the certification-ledger query below
  // gates on the role and must not fire for someone the API will 403.
  const { hasVertical, user } = useAuth();
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferYardId, setTransferYardId] = useState('');
  const [transferType, setTransferType] = useState<'shop' | 'rig'>('shop');
  const [transferRig, setTransferRig] = useState('');
  const [showEdit, setShowEdit] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMaintenanceDialog, setShowMaintenanceDialog] = useState(false);
  const [editingMaintenanceRecord, setEditingMaintenanceRecord] = useState<MaintenanceRecord | null>(null);
  const [downloadingRecordPdf, setDownloadingRecordPdf] = useState<string | null>(null);
  const certFileRef = useRef<HTMLInputElement>(null);
  const [certUploading, setCertUploading] = useState(false);
  const photoFileRef = useRef<HTMLInputElement>(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [localPhotoUrl, setLocalPhotoUrl] = useState<string | null | undefined>(undefined);
  const [showPhotoDeleteConfirm, setShowPhotoDeleteConfirm] = useState(false);
  const [tab, setTab] = useState<VehicleTab>('overview');

  const { data: eqData, isLoading } = useQuery({
    queryKey: ['equipment', equipmentId],
    queryFn: () => api.equipment.get(equipmentId!).then(r => r.data.data ?? r.data),
    enabled: !!equipmentId,
  });

  const equipment = eqData as any;

  const { data: usageData } = useQuery({
    queryKey: ['equipment-usage', equipmentId],
    queryFn: () => api.equipment.usage(equipmentId!).then(r => r.data),
    enabled: !!equipmentId && !!equipment?.certDayLimit,
  });

  const { data: certUploadData } = useQuery({
    queryKey: ['cert-uploads', equipmentId],
    queryFn: () => api.uploads.listForEntity('equipment_cert', equipmentId!).then(r => {
      const uploads = r.data.data ?? r.data ?? [];
      return uploads.length > 0 ? uploads[uploads.length - 1] : null;
    }),
    enabled: !!equipmentId && !!equipment?.certDayLimit,
  });

  const usage = usageData as any;
  // Both figures come straight off GET /equipment/:id/usage. The count is the
  // API's own usageLogTotalCount, so a capped response cannot understate it,
  // and it stays null until the response arrives — an unknown count renders as
  // an em dash rather than a confident 0.
  const usageRows: any[] = Array.isArray(usage?.usageLog) ? usage.usageLog : [];
  // ── THE COUNT HAS TO COUNT WHAT THE TABLE SHOWS ──────────────────────────
  //
  // `usageLogTotalCount` is documented "Excludes adjustments" — it counts
  // TICKET rows only, and always has. The table beneath it renders both halves.
  // On a unit whose days are all hand-entered that reads "0 records" directly
  // above 107 rows, which is the silent-blank-field family stated as a count:
  // a confident number that is the opposite of the truth.
  //
  // Seven of TNT's ten units with hand-entered days have zero ticket rows, so
  // this is the ordinary case for the tenant the ledger was built for, not an
  // edge.
  //
  // Both figures are reported, never summed into one. They are different kinds
  // of claim — a day the system counted off an approved ticket and a day
  // somebody typed off a paper log — and the whole cert surface is built on
  // keeping them tellable apart.
  const usageTicketCount: number | null = usage
    ? (usage.usageLogTotalCount ?? null)
    : null;
  const usageAdjustmentCount: number | null = usage
    ? (usage.adjustmentCount ?? null)
    : null;

  // ── The append-only certification-day ledger (API migration 0356) ─────────
  //
  // A SECOND query rather than reading it off `usage`, because the two answer
  // different questions and are gated differently. `usage` is the log a field
  // hand sees — every day this tool worked, hand-entered days marked. This is
  // the office record: who typed what, when, and what they later corrected. The
  // API refuses it outside {admin, office_staff, dispatcher}, so it is not even
  // fired for anyone else.
  const certWritableUser = canWriteCertFields(user?.role);
  // Upload and Remove on the certificate PDF: a wider list than the counter
  // (supervisor is on it). See canManageEquipmentCertDocuments.
  const certDocsWritable = canManageEquipmentCertDocuments(user?.role);
  /**
   * A request to the ledger below to tick rows and scroll to itself.
   *
   * The Usage Log renders the same hand-entered days the ledger does, and can
   * only CORRECT them — which is the wrong repair when the entries are on the
   * wrong unit, the one case a correction cannot express. TNT hit exactly that
   * with 67 entries, saw 67 pencils, and never reached the ledger underneath.
   * These hand the selection down instead of describing where to scroll.
   */
  const [ledgerFocus, setLedgerFocus] = useState<{ ids: string[]; nonce: number } | null>(null);
  const focusInLedger = (ids: string[]) =>
    setLedgerFocus({ ids, nonce: Date.now() });
  const { data: certLedger } = useCertAdjustments(equipmentId, certWritableUser);
  // Memoised. A bare `?? []` mints a fresh empty array on every render, so the
  // Map below would rebuild every time and the dependency would never settle —
  // the exhaustive-deps warning is pointing at a real re-render, not being
  // pedantic.
  const adjustments: CertAdjustmentRecord[] = React.useMemo(
    () => certLedger?.adjustments ?? [],
    [certLedger]
  );
  // `null` until the read lands, and `null` forever against an API that does
  // not carry it. Not `{ residual: 0 }` — "the counter adds up" is a finding,
  // and asserting it from an absent response is the one wrong answer here.
  const reconciliation = certLedger?.reconciliation ?? null;
  /** Live hand-entered entries, in ledger order — what the Usage Log offers to tick. */
  const liveHandEnteredIds = React.useMemo(
    () => adjustments.filter((a) => !a.deletedAt).map((a) => a.id),
    [adjustments]
  );
  const adjustmentsById = React.useMemo(
    () => new Map(adjustments.map((a) => [a.id, a])),
    [adjustments]
  );
  const [correcting, setCorrecting] = useState<CertAdjustmentRecord | null>(null);

  const { data: maintenanceData, refetch: refetchMaintenance } = useQuery({
    queryKey: ['equipment-maintenance', equipmentId],
    queryFn: () => api.equipmentMaintenance.list({ equipment_id: equipmentId! }).then(r => {
      const d = r.data?.data ?? r.data;
      return Array.isArray(d) ? d : [];
    }),
    enabled: !!equipmentId,
  });
  const maintenanceRecords: MaintenanceRecord[] = maintenanceData ?? [];

  const { data: yardsData } = useQuery({
    queryKey: ['service-locations'],
    queryFn: () => api.serviceLocations.list().then(r => r.data.data ?? r.data ?? []),
  });
  const yards: any[] = yardsData ?? [];
  const yardMap: Record<string, any> = {};
  yards.forEach((y: any) => { yardMap[y.id] = y; });

  const { data: jobsData } = useQuery({
    queryKey: ['jobs-for-transfer'],
    queryFn: () => api.jobs.list().then(r => {
      const all = r.data.data ?? r.data ?? [];
      return Array.isArray(all) ? all.filter((j: any) => ['scheduled', 'active', 'pending'].includes(j.status)) : [];
    }),
  });
  const activeJobs: any[] = Array.isArray(jobsData) ? jobsData : [];

  const transferMutation = useMutation({
    mutationFn: ({ id, toYardId, toRig }: { id: string; toYardId?: string; toRig?: string }) =>
      apiClient.post(`/equipment/${id}/transfer`, toRig ? { toRig } : { toYardId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment', equipmentId] });
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      queryClient.invalidateQueries({ queryKey: MAINTENANCE_FLAGS_ROOT });
      setShowTransfer(false);
      setTransferYardId('');
    },
  });

  const editMutation = useMutation({
    mutationFn: (data: Record<string, any>) => api.equipment.update(equipmentId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment', equipmentId] });
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      queryClient.invalidateQueries({ queryKey: MAINTENANCE_FLAGS_ROOT });
      setShowEdit(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.equipment.delete(equipmentId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      navigate('/equipment', { replace: true });
    },
  });

  const [certError, setCertError] = useState('');
  const { isMetric } = useLocale();
  const timeZone = useTenantTimeZone();

  const handleCertUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !equipmentId) return;
    setCertUploading(true);
    setCertError('');
    try {
      // Step 1: Get presigned URL
      const { data: presigned } = await api.uploads.getPresignedUrl({
        fileName: file.name,
        contentType: file.type || 'application/pdf',
        entityType: 'equipment_cert',
        entityId: equipmentId,
      });
      // Step 2: Upload directly to S3
      await putToPresignedUrl(presigned.uploadUrl, file, file.type || 'application/pdf');
      // Step 3: Confirm upload
      await api.uploads.confirm({ key: presigned.key, size: file.size });
      queryClient.invalidateQueries({ queryKey: ['cert-uploads', equipmentId] });
    } catch (err: any) {
      console.error('Cert upload failed:', err);
      setCertError(err?.response?.data?.error || err?.message || 'Upload failed. Please try again.');
    } finally {
      setCertUploading(false);
      if (certFileRef.current) certFileRef.current.value = '';
    }
  };

  const handleViewCert = () => {
    if (!certUploadData) return;
    void openUploadDownload(certUploadData.id);
  };

  // Remove deletes the ONE document on screen — `certUploadData`, the latest
  // equipment_cert upload — by its id. It used to list every equipment_cert
  // upload on the unit and delete them all, which also took the history under
  // the shown one and, since the pressure-control screen files its
  // certificate as `equipment_cert` against the same unit, could take a
  // pressure-control certificate's document too. The API now refuses (409) a
  // file a record still holds; that refusal is said beside the certificate,
  // where it stays, rather than in a toast that goes away.
  const handleCertRemove = async () => {
    if (!equipmentId || !certUploadData?.id) return;
    setCertError('');
    try {
      await api.uploads.delete(certUploadData.id);
      queryClient.invalidateQueries({ queryKey: ['cert-uploads', equipmentId] });
    } catch (err: any) {
      console.error('Cert delete failed:', err);
      const message = err?.response?.data?.error;
      if (err?.response?.status === 409) {
        setCertError(message || 'This certificate belongs to another record and cannot be removed here.');
      } else {
        toast.error(message || 'Could not remove the certificate');
      }
      queryClient.invalidateQueries({ queryKey: ['cert-uploads', equipmentId] });
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !equipmentId) return;
    setPhotoUploading(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const res = await apiClient.post(`/equipment/${equipmentId}/photo`, formData);
      const photoUrl = res.data.photo_url ?? res.data.photoUrl;
      setLocalPhotoUrl(photoUrl);
      queryClient.invalidateQueries({ queryKey: ['equipment', equipmentId] });
      toast.success('Photo uploaded');
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Photo upload failed';
      toast.error(msg);
    } finally {
      setPhotoUploading(false);
      if (photoFileRef.current) photoFileRef.current.value = '';
    }
  };

  const handlePhotoDelete = async () => {
    if (!equipmentId) return;
    try {
      await apiClient.delete(`/equipment/${equipmentId}/photo`);
      setLocalPhotoUrl(null);
      setShowPhotoDeleteConfirm(false);
      queryClient.invalidateQueries({ queryKey: ['equipment', equipmentId] });
      toast.success('Photo removed');
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to remove photo';
      toast.error(msg);
    }
  };

  const handleDownloadPdf = async () => {
    if (!equipmentId) return;
    try {
      const res = await api.reports.equipmentCertPdfSingle(equipmentId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${equipment?.serialNumber || equipment?.name || 'equipment'}-cert-usage.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed:', err);
    }
  };

  const handleDownloadCsv = async () => {
    try {
      const res = await api.reports.equipmentCertCsv();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'equipment-cert-usage.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV download failed:', err);
    }
  };

  const handleDownloadMaintenancePdf = async (recordId: string, unitNumber?: string) => {
    setDownloadingRecordPdf(recordId);
    try {
      const res = await api.equipmentMaintenance.recordPdf(recordId);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `maintenance-${unitNumber || recordId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download error:', err);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingRecordPdf(null);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-full bg-fieldiq-dark">
        <BackHeader to="/equipment" label="Equipment" title="Loading..." />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" />
        </div>
      </div>
    );
  }

  if (!equipment) {
    return (
      <div className="min-h-full bg-fieldiq-dark">
        <BackHeader to="/equipment" label="Equipment" title="Not Found" />
        <div className="text-center py-20 text-fieldiq-text">Equipment not found.</div>
      </div>
    );
  }

  const STATUS_CFG: Record<string, { label: string; bg: string; text: string }> = {
    available:    { label: 'Available',      bg: 'bg-green-500/20', text: 'text-green-400' },
    reserved:     { label: 'Reserved',       bg: 'bg-purple-500/20', text: 'text-purple-400' },
    in_use:       { label: 'In Use',         bg: 'bg-blue-500/20',  text: 'text-blue-400' },
    left_on_site: { label: 'On Location',    bg: 'bg-amber-500/20', text: 'text-amber-400' },
    maintenance:  { label: 'In Maintenance', bg: 'bg-red-500/20',   text: 'text-red-400' },
    retired:      { label: 'Retired',        bg: 'bg-gray-500/20',  text: 'text-gray-400' },
  };
  const displayStatus = (equipment.status === 'available' && equipment.currentJobId) ? 'reserved' : equipment.status;
  const sCfg = STATUS_CFG[displayStatus] || STATUS_CFG.available;

  // asset_kind drives both the tabbed vehicle layout and the maintenance
  // trigger picker (vehicles get meter triggers, equipment gets job_count).
  const assetKind: string = equipment.assetKind ?? equipment.asset_kind ?? 'equipment';
  const isVehicle = assetKind === 'vehicle';
  const odometerKey = isMetric ? 'odometer_km' : 'odometer_mi';
  const distanceUnit = isMetric ? 'km' : 'mi';

  // New API shape: current_meters keyed by reading_type
  const currentMeters: CurrentMeters | null = equipment.currentMeters ?? equipment.current_meters ?? null;
  // Backwards compat with old single-meter shape
  const currentMeter: CurrentMeter | null = equipment.currentMeter ?? equipment.current_meter ?? null;

  const odometerReading = currentMeters?.[odometerKey] ?? currentMeters?.odometer_km ?? currentMeters?.odometer_mi ?? null;
  const engineHoursReading = currentMeters?.engine_hours ?? null;
  // Fallback to legacy single-meter
  const odometerValue = odometerReading?.value ?? (currentMeter?.type?.startsWith('odometer') && currentMeter.value != null ? currentMeter.value : null);
  const odometerDate = odometerReading?.reading_date ?? (currentMeter?.type?.startsWith('odometer') ? currentMeter.reading_date : null);
  const engineHoursValue = engineHoursReading?.value ?? (currentMeter?.type === 'engine_hours' && currentMeter.value != null ? currentMeter.value : null);
  const engineHoursDate = engineHoursReading?.reading_date ?? (currentMeter?.type === 'engine_hours' ? currentMeter.reading_date : null);

  const licensePlate = equipment.licensePlate ?? equipment.license_plate ?? null;
  // Non-vehicle assets always render the full page (no tabs)
  const showSection = (t: VehicleTab) => !isVehicle || tab === t;

  // THE LEDGER COUNTER, AND ONLY THE LEDGER COUNTER.
  //
  // This used to nullish-coalesce the counter through the usage payload and
  // then through a literal zero, and BOTH fallbacks produced the same wrong
  // answer for an untracked tool. The literal is the obvious one. The usage
  // payload's own days-used figure is not: it is the summed usage rows,
  // which for a tool with no approved tickets is also 0 — so dropping only the
  // literal zero left the card still reading "0 of 500 working days" for a tool
  // nobody has ever inspected. Verified in a browser, not reasoned about.
  //
  // The API offers `effectiveDaysUsed` (`workingDays ?? sumDaysUsed`) for this
  // card, but that expression cannot represent "unknown" either — it collapses
  // an unseeded counter and a genuinely fresh one onto the same 0. Reported for
  // the API rather than patched around here.
  //
  // So this card now reads `working_days_since_inspection`, which is the column
  // its own label names and the column the other five cert-day surfaces read.
  // A legacy row with usage rows but no counter renders `—` instead of the
  // summed figure — accurate, and the gap is the point.
  const daysUsed = equipment.workingDaysSinceInspection ?? null;
  const dayLimit = equipment.certDayLimit;
  // Uncapped on purpose — a tool past its limit should say 140%, and the bar
  // clamps its own width. See lib/cert-days.ts.
  const pct = certDayPercent({ daysUsed, dayLimit });
  // CAODC cert-day tracking is equipment-only (same rule the Add/Edit modals
  // enforce). Boolean-coerced on purpose: a bare `dayLimit &&` JSX guard renders
  // a stray "0" whenever the API returns certDayLimit: 0 instead of null.
  const showCertTracking = !isVehicle && !!dayLimit;
  // Read the level off the stored description rather than deriving it from the
  // day limit — but repair the rows the old Custom button left holding a stale
  // CAODC level. See lib/equipmentCertLevel.ts.
  const certLevel = certLevelLabel(equipment.certDescription, dayLimit);
  const displayName = equipment.serialNumber || equipment.name;

  // Location: "at Rig X" (in field) or "at GP Shop" (available)
  let locationLabel: string | null = null;
  if (equipment.status === 'in_use' || equipment.status === 'left_on_site') {
    const jobWell = equipment.linked_job_well || equipment.linkedJobWell;
    locationLabel = jobWell ? `at Rig ${jobWell}` : equipment.lastKnownRig ? `at Rig ${equipment.lastKnownRig}` : null;
  } else {
    const yard = equipment.currentYardId ? yardMap[equipment.currentYardId] : equipment.homeYardId ? yardMap[equipment.homeYardId] : null;
    locationLabel = yard ? `at ${yard.name}` : null;
  }

  // Same place, without the "at " that only reads well mid-sentence — this one
  // seeds a form field.
  const plainLocation = locationLabel ? locationLabel.replace(/^at /, '') : null;

  // Hand the inspection form everything it would otherwise have to wait for the
  // equipment-list query to tell it. Keys are snake_case because that is the
  // casing the structured form reads first.
  const startInspection = () => {
    navigate('/safety/inspection', {
      state: {
        equipmentId: equipment.id ?? equipmentId,
        equipmentType: equipment.type ?? null,
        equipmentData: {
          name: equipment.name ?? null,
          asset_number: equipment.unitNumber ?? equipment.unit_number ?? null,
          manufacturer: equipment.manufacturer ?? null,
          model: equipment.model ?? null,
          serial_number: equipment.serialNumber ?? equipment.serial_number ?? null,
          size: equipment.size ?? null,
          capacity: equipment.capacity ?? null,
          location: plainLocation,
        },
      },
    });
  };

  const renderPhoto = () => {
    const photoUrl = localPhotoUrl !== undefined ? localPhotoUrl : (equipment.photoUrl ?? equipment.photo_url ?? null);
    if (photoUploading) {
      return (
        <div className="relative w-full max-h-[300px] min-h-[200px] rounded-lg bg-fieldiq-dark border border-fieldiq-border flex items-center justify-center mb-4">
          <div className="flex flex-col items-center gap-2 text-fieldiq-muted">
            <Loader2 className="w-8 h-8 animate-spin text-fieldiq-accent" />
            <span className="text-sm">Uploading photo...</span>
          </div>
        </div>
      );
    }
    if (photoUrl) {
      return (
        <div className="relative group mb-4">
          <img
            src={photoUrl}
            alt={equipment.name}
            className="w-full max-h-[300px] object-cover rounded-lg"
          />
          <div className="absolute bottom-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity sm:opacity-100">
            <Tooltip content="Replace photo">
              <button
                onClick={() => photoFileRef.current?.click()}
                className="flex items-center gap-1.5 bg-fieldiq-card/90 backdrop-blur border border-fieldiq-border text-white text-xs font-medium px-3 py-2 rounded-lg hover:bg-fieldiq-card transition-colors min-h-[44px]"
              >
                <RefreshCw size={14} />
                Replace
              </button>
            </Tooltip>
            <Tooltip content="Remove photo">
              <button
                onClick={() => setShowPhotoDeleteConfirm(true)}
                className="flex items-center gap-1.5 bg-fieldiq-card/90 backdrop-blur border border-red-500/30 text-red-400 text-xs font-medium px-3 py-2 rounded-lg hover:bg-red-500/20 transition-colors min-h-[44px]"
              >
                <Trash2 size={14} />
                Remove
              </button>
            </Tooltip>
          </div>
        </div>
      );
    }
    return (
      <button
        onClick={() => photoFileRef.current?.click()}
        className="w-full min-h-[200px] max-h-[300px] rounded-lg border-2 border-dashed border-fieldiq-border bg-fieldiq-dark/50 flex flex-col items-center justify-center gap-2 mb-4 hover:border-fieldiq-accent hover:bg-fieldiq-dark transition-colors cursor-pointer"
      >
        <Camera className="w-10 h-10 text-fieldiq-muted" />
        <span className="text-sm text-fieldiq-muted font-medium">Add Photo</span>
      </button>
    );
  };

  return (
    <div className="min-h-full bg-fieldiq-dark">
      <BackHeader
        to="/equipment"
        label="Equipment"
        title={`${displayName} Details`}
        rightAction={
          <div className="flex items-center gap-2">
            {hasVertical('casing_running') && (
              <Tooltip content="Run inspection" side="bottom">
                <button onClick={startInspection} className="p-2 rounded-lg hover:bg-white/5 transition-colors" aria-label="Run inspection">
                  <ClipboardCheck size={18} className="text-fieldiq-accent" />
                </button>
              </Tooltip>
            )}
            <Tooltip content="Edit" side="bottom">
              <button onClick={() => setShowEdit(true)} className="p-2 rounded-lg hover:bg-white/5 transition-colors" aria-label="Edit">
                <Pencil size={18} className="text-fieldiq-accent" />
              </button>
            </Tooltip>
            <Tooltip content="Transfer" side="bottom">
              <button onClick={() => { setShowTransfer(true); setTransferYardId(''); setTransferRig(''); setTransferType('shop'); }} className="p-2 rounded-lg hover:bg-white/5 transition-colors" aria-label="Transfer">
                <ArrowRightLeft size={18} className="text-fieldiq-accent" />
              </button>
            </Tooltip>
            {/* Numeric guard: certDayLimit is a number, so a bare `&&` renders a
                stray "0" in the action row when the API returns 0 instead of null. */}
            {!!equipment.certDayLimit && (
              <>
                <Tooltip content="Download PDF" side="bottom">
                  <button onClick={handleDownloadPdf} className="p-2 rounded-lg hover:bg-white/5 transition-colors" aria-label="Download PDF">
                    <FileText size={18} className="text-fieldiq-accent" />
                  </button>
                </Tooltip>
                <Tooltip content="Download CSV" side="bottom">
                  <button onClick={handleDownloadCsv} className="p-2 rounded-lg hover:bg-white/5 transition-colors" aria-label="Download CSV">
                    <Download size={18} className="text-fieldiq-accent" />
                  </button>
                </Tooltip>
              </>
            )}
            <Tooltip content="Delete" side="bottom" align="end">
              <button onClick={() => setShowDeleteConfirm(true)} className="p-2 rounded-lg hover:bg-red-500/10 transition-colors" aria-label="Delete">
                <Trash2 size={18} className="text-red-400" />
              </button>
            </Tooltip>
          </div>
        }
      />

      <main className="max-w-[1920px] mx-auto px-4 py-4 pb-[var(--bottom-chrome)] space-y-4">
        <input
          ref={photoFileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handlePhotoUpload}
        />

        {/* Vehicle meter summary */}
        {isVehicle && (
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-fieldiq-card rounded-lg border border-fieldiq-border p-4">
              <p className="text-xs text-fieldiq-muted">Odometer ({distanceUnit})</p>
              <p className="text-xl font-bold text-white mt-0.5">
                {odometerValue != null
                  ? `${Number(odometerValue).toLocaleString()} ${distanceUnit}`
                  : '—'}
              </p>
              {odometerValue != null && odometerDate && (
                <p className="text-xs text-fieldiq-muted mt-1">
                  as of {formatCalendarDate(odometerDate, {}, 'en-CA')}
                </p>
              )}
            </div>
            <div className="bg-fieldiq-card rounded-lg border border-fieldiq-border p-4">
              <p className="text-xs text-fieldiq-muted">Engine hours</p>
              <p className="text-xl font-bold text-white mt-0.5">
                {engineHoursValue != null
                  ? `${Number(engineHoursValue).toLocaleString()} hrs`
                  : '—'}
              </p>
              {engineHoursValue != null && engineHoursDate && (
                <p className="text-xs text-fieldiq-muted mt-1">
                  as of {formatCalendarDate(engineHoursDate, {}, 'en-CA')}
                </p>
              )}
            </div>
          </div>
        )}

        {/* A non-vehicle unit (an excavator, a pump) has no odometer, but a
            field daily log can record its hour-meter end (equipment_meter_readings,
            source daily_log). Show the latest reading when one exists; a unit with
            no reading renders exactly as before. */}
        {!isVehicle && engineHoursValue != null && (
          <div className="bg-fieldiq-card rounded-lg border border-fieldiq-border p-4" data-testid="equipment-hour-meter">
            <p className="text-xs text-fieldiq-muted">Hour meter</p>
            <p className="text-xl font-bold text-white mt-0.5">{`${Number(engineHoursValue).toLocaleString()} hrs`}</p>
            {engineHoursDate && (
              <p className="text-xs text-fieldiq-muted mt-1">
                as of {formatCalendarDate(engineHoursDate, {}, 'en-CA')}
              </p>
            )}
          </div>
        )}

        {/* Vehicle tabs */}
        {isVehicle && (
          <div className="flex gap-1 border-b border-fieldiq-border overflow-x-auto">
            {VEHICLE_TABS.map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-3 py-2 text-sm capitalize whitespace-nowrap transition-colors border-b-2 -mb-px ${
                  tab === t
                    ? 'border-fieldiq-accent text-white font-medium'
                    : 'border-transparent text-fieldiq-muted hover:text-white'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        )}

        {/* Documents (vehicle only) */}
        {isVehicle && tab === 'documents' && <AssetDocuments equipmentId={equipment.id} />}

        {/* Photos (vehicle only) */}
        {isVehicle && tab === 'photos' && (
          <div className="bg-fieldiq-card rounded-lg border border-fieldiq-border p-4">
            <h3 className="text-base font-bold text-white mb-3">Photos</h3>
            {renderPhoto()}
          </div>
        )}

        {/* Expenses (vehicle only) */}
        {isVehicle && tab === 'expenses' && <AssetExpenses equipmentId={equipment.id} />}

        {/* Info Card */}
        {showSection('overview') && (
        <div className="bg-fieldiq-card rounded-lg border border-fieldiq-border p-4">
          {!isVehicle && renderPhoto()}

          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-fieldiq-accent/10 rounded-lg">
              <Settings className="w-6 h-6 text-fieldiq-accent" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-white">{equipment.name}</h2>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${sCfg.bg} ${sCfg.text}`}>
                {sCfg.label}
              </span>
            </div>
          </div>

          <div className="space-y-3">
            {!!equipment.serialNumber && (
              <div className="flex items-center justify-between py-2 border-b border-fieldiq-border">
                <span className="text-fieldiq-text text-sm">Serial Number</span>
                <span className="font-medium text-white text-sm">{equipment.serialNumber}</span>
              </div>
            )}
            <div className="flex items-center justify-between py-2 border-b border-fieldiq-border">
              <span className="text-fieldiq-text text-sm">Type</span>
              <span className="font-medium text-white text-sm">{equipment.type}</span>
            </div>
            {isVehicle && (
              <>
                <div className="flex items-center justify-between py-2 border-b border-fieldiq-border">
                  <span className="text-fieldiq-text text-sm">License Plate</span>
                  <span className="font-medium text-white text-sm">{licensePlate || '—'}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-fieldiq-border">
                  <span className="text-fieldiq-text text-sm">VIN</span>
                  <span className="font-medium text-white text-sm font-mono">{equipment.vin || '—'}</span>
                </div>
              </>
            )}
            {equipment.manufacturer && (
              <div className="flex items-center justify-between py-2 border-b border-fieldiq-border">
                <span className="text-fieldiq-text text-sm">Manufacturer</span>
                <span className="font-medium text-white text-sm">{equipment.manufacturer}</span>
              </div>
            )}
            {!isVehicle && !!equipment.size && (
              <div className="flex items-center justify-between py-2 border-b border-fieldiq-border">
                <span className="text-fieldiq-text text-sm">Size</span>
                <span className="font-medium text-white text-sm">{equipment.size}</span>
              </div>
            )}
            {!isVehicle && !!equipment.capacity && (
              <div className="flex items-center justify-between py-2 border-b border-fieldiq-border">
                <span className="text-fieldiq-text text-sm">Capacity</span>
                <span className="font-medium text-white text-sm">{equipment.capacity}</span>
              </div>
            )}
            {/* Location */}
            {locationLabel && (
              <div className="flex items-center justify-between py-2 border-b border-fieldiq-border">
                <span className="text-fieldiq-text text-sm">Location</span>
                <span className="font-medium text-white text-sm flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {locationLabel}
                </span>
              </div>
            )}
            {/* The asset's latest assignment, open or closed (api#985). Nothing
                when it was never on a job. */}
            <LastJobLine equipmentId={equipment.id} lastJob={parseLastJob(equipment)} variant="office" />
            {/* Both inspection dates are INSTANTS (api#838) and printed the raw ISO
                string here. Shown as the tenant's day. */}
            {!isVehicle && equipment.lastInspectionDate && (
              <div className="flex items-center justify-between py-2 border-b border-fieldiq-border">
                <span className="text-fieldiq-text text-sm">Last Inspection</span>
                <span className="font-medium text-white text-sm flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatInstantTenantDay(equipment.lastInspectionDate, timeZone) ?? '\u2014'}
                </span>
              </div>
            )}
            {!isVehicle && equipment.nextInspectionDate && (
              <div className="flex items-center justify-between py-2 border-b border-fieldiq-border">
                <span className="text-fieldiq-text text-sm">Next Inspection</span>
                <span className="font-medium text-fieldiq-accent text-sm flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatInstantTenantDay(equipment.nextInspectionDate, timeZone) ?? '\u2014'}
                </span>
              </div>
            )}
            {/* The effective cadence and where it came from (FE-5), from the
                same resolver the maintenance agent reads. */}
            {!isVehicle && (
              <InspectionCadenceLine equipmentId={equipment.id} canWrite={canWriteServicePolicies(user?.role)} />
            )}
            {/* Same sentence as the Inspections bar below and as every other
                cert-day counter in the app. This row used to spell it
                `30 / 500` with no unit and its own hand-rolled 0.9/0.75
                thresholds, which disagreed with the shared rule across a
                rounding boundary.

                It reads the same `daysUsed` the bar does, so the two figures
                on this page cannot differ. They could before: the bar fell back
                to the API's summed usage rows when the ledger counter was
                absent and this row did not. */}
            {!isVehicle && (
              <div className="flex items-center justify-between py-2 border-b border-fieldiq-border">
                <span className="text-fieldiq-text text-sm">Working Days Since Inspection</span>
                <span className={`font-bold text-sm ${certDayTextColor(pct)}`}>
                  {formatCertDays({ daysUsed, dayLimit }, 'used')}
                </span>
              </div>
            )}
            {equipment.notes && (
              <div className="py-2">
                <span className="text-fieldiq-text text-sm">Notes</span>
                <p className="font-medium text-white text-sm mt-1">{equipment.notes}</p>
              </div>
            )}
          </div>
        </div>
        )}

        {/* Verify & Check Out */}
        {showSection('overview') && (
        <div className="mt-4">
          <VerifyCheckOutButton
            equipmentId={equipment.id}
            hasReferencePhoto={!!(equipment.photoUrl ?? equipment.photo_url)}
          />
        </div>
        )}

        {/* Maintenance checks (api#944). In the body beside the cert block,
            never on the band. Renders nothing outside suggest mode. */}
        {showSection('overview') && equipmentId && (
          <MaintenanceFlagsPanel subject={{ kind: 'equipment', id: equipmentId }} />
        )}

        {/* Pressure control (api#1268): renders only when the API says this unit
            is a registered pressure-control component. */}
        {showSection('overview') && equipmentId && <PressureControlPanel equipmentId={equipmentId} />}

        {/* Cert Tracking Card */}
        {showSection('overview') && showCertTracking && (
          <div className="bg-fieldiq-card rounded-lg border border-fieldiq-border p-4">
            <div className="flex items-center gap-2 mb-3">
              <Shield size={18} className="text-fieldiq-accent" />
              <h3 className="text-base font-bold text-white">Inspections</h3>
            </div>

            {certLevel && (
              <p className="text-sm text-fieldiq-text mb-3">{certLevel}</p>
            )}

            {/* Progress bar */}
            <CertDayCounter
              daysUsed={daysUsed}
              dayLimit={dayLimit}
              mode="used"
              percentUsed={pct}
              className="mb-3"
            />

            <div className="grid grid-cols-2 gap-3 text-sm">
              {equipment.certStartDate && (
                <div className="bg-fieldiq-dark rounded-lg p-3">
                  <span className="text-fieldiq-muted text-xs">Start Date</span>
                  <p className="text-white font-medium mt-0.5">{equipment.certStartDate}</p>
                </div>
              )}
              {equipment.certExpiryDate && (
                <div className="bg-fieldiq-dark rounded-lg p-3">
                  <span className="text-fieldiq-muted text-xs">Expiry Date</span>
                  <p className="text-white font-medium mt-0.5">{equipment.certExpiryDate}</p>
                </div>
              )}
            </div>

            {/* Cert PDF upload / view */}
            <div className="mt-3">
              <input ref={certFileRef} type="file" accept="application/pdf" className="hidden" onChange={handleCertUpload} />
              {certError && (
                <div className="text-sm text-red-400 bg-red-900/20 rounded-lg px-3 py-2 mb-2">{certError}</div>
              )}
              {certUploading ? (
                <div className="flex items-center justify-center gap-2 text-sm text-fieldiq-text py-2.5">
                  <Loader2 size={16} className="animate-spin" /> Uploading certificate...
                </div>
              ) : certUploadData ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 bg-fieldiq-dark rounded-lg px-3 py-2.5 border border-fieldiq-border">
                    <FileText size={16} className="text-green-400 flex-shrink-0" />
                    <span className="text-sm text-white truncate flex-1">{certUploadData.originalName || certUploadData.original_name || 'Certificate.pdf'}</span>
                    <Tooltip content="View">
                      <button onClick={handleViewCert} className="text-fieldiq-accent hover:text-amber-300" aria-label="View">
                        <ExternalLink size={14} />
                      </button>
                    </Tooltip>
                    {certDocsWritable && (
                      <Tooltip content="Remove">
                        <button onClick={handleCertRemove} className="text-fieldiq-muted hover:text-red-400" aria-label="Remove">
                          <X size={14} />
                        </button>
                      </Tooltip>
                    )}
                  </div>
                  <button
                    onClick={handleViewCert}
                    className="w-full flex items-center justify-center gap-2 bg-green-500/20 text-green-400 py-2.5 rounded-lg text-sm font-medium hover:bg-green-500/30 transition-colors"
                  >
                    <FileText size={16} /> View Inspection Certificate
                    <ExternalLink size={14} />
                  </button>
                </div>
              ) : certDocsWritable ? (
                <button
                  onClick={() => certFileRef.current?.click()}
                  className="w-full border-2 border-dashed border-fieldiq-border rounded-lg py-3 text-sm text-fieldiq-muted hover:border-fieldiq-accent hover:text-fieldiq-accent transition-colors flex items-center justify-center gap-2"
                >
                  <Upload size={16} /> Upload Inspection Certificate (PDF)
                </button>
              ) : null}
            </div>

            {/* The append-only ledger's entry forms. Inside this card rather
                than a card of their own: an opening balance and a historical
                day are statements about the certification budget the bar above
                is drawing, and separating them would make the number and the
                explanation two different places on the screen. Role-gated to
                match the API — the buttons are not shown to somebody whose
                save would answer 403. */}
            {certWritableUser && (
              <CertAdjustmentEntryPanel
                equipmentId={equipment.id}
                hasOpeningBalance={adjustments.some((a) => a.kind === 'opening_balance')}
                counter={equipment.workingDaysSinceInspection ?? null}
              />
            )}
          </div>
        )}

        {/* Sign-out history (api#985): where the asset went and who moved it.
            Not the Usage Log below, which counts cert days. */}
        {showSection('overview') && equipment.id && (
          <EquipmentHistoryCard equipmentId={equipment.id} variant="office" />
        )}

        {/* Usage Log — no row-count gate.
            An empty log is a fact about the data: no approved tickets have
            landed in this cert cycle yet. Suppressing the whole card for that
            reads as "this tool has no usage log", which is the opposite of the
            truth for a tool that HAS a cert budget — the same mistake already
            corrected on the field screen. So it renders whenever cert tracking
            does, and says why it is empty. */}
        {showSection('overview') && showCertTracking && (
          <div className="bg-fieldiq-card rounded-lg border border-fieldiq-border overflow-hidden">
            <div className="p-4 border-b border-fieldiq-border">
              <h3 className="text-base font-bold text-white">Usage Log</h3>
              {/* Identified: "N records" is also the Maintenance History card's
                  count, so an assertion about THIS line needs a way to say so.
                  A document-wide search for it matches the wrong card. */}
              {/* The way out of this table.

                  Every hand-entered row here offers a pencil and nothing else,
                  and a correction cannot say "this belongs on another unit" —
                  applyCertAdjustment refuses one naming a row on different
                  equipment. So the table that makes the wrong-unit mistake
                  visible is the one table that cannot repair it, and the fix
                  is a scroll away under a different heading. This says so, and
                  arrives with the rows already ticked.

                  Counted off the LEDGER, not off usageRows: the usage response
                  is capped and would understate the number on exactly the unit
                  where it matters most. */}
              {certWritableUser && liveHandEnteredIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => focusInLedger(liveHandEnteredIds)}
                  data-testid="usage-log-select-hand-entered"
                  className="mt-1.5 text-xs text-fieldiq-accent hover:underline text-left"
                >
                  Select {liveHandEnteredIds.length === 1
                    ? 'the 1 hand-entered entry'
                    : `all ${liveHandEnteredIds.length} hand-entered entries`}
                  {' '}in the Certification Day Ledger below, where they can be removed →
                </button>
              )}
              <p className="text-xs text-fieldiq-text mt-0.5" data-testid="usage-log-count">
                {usageTicketCount === null ? '—' : (() => {
                  const t = usageTicketCount;
                  const a = usageAdjustmentCount ?? 0;
                  const fromTickets = `${t} from ticket${t === 1 ? '' : 's'}`;
                  // Both halves named, and the hand-entered half named only
                  // when there is one — a trailing "· 0 entered by hand" on
                  // every ordinary tool is noise that trains people not to
                  // read the line.
                  return a > 0
                    ? `${fromTickets} · ${a} entered by hand`
                    : fromTickets;
                })()}
              </p>
            </div>

            {usageRows.length > 0 ? (
              <div className="overflow-x-auto">
                {/* Identified, because the ledger below renders the same dates
                    and the same units for a different purpose. A test — or a
                    reader — has to be able to say which table it means. */}
                <table className="w-full text-sm" data-testid="usage-log-table">
                  <thead>
                    <tr className="bg-fieldiq-dark text-fieldiq-muted text-xs uppercase">
                      <th className="text-left px-4 py-2.5 font-medium">Date</th>
                      <th className="text-left px-4 py-2.5 font-medium">Client</th>
                      <th className="text-left px-4 py-2.5 font-medium">Rig</th>
                      <th className="text-left px-4 py-2.5 font-medium">Ticket #</th>
                      <th className="text-left px-4 py-2.5 font-medium">Job #</th>
                      <th className="text-left px-4 py-2.5 font-medium">Well</th>
                      <th className="text-left px-4 py-2.5 font-medium">Days</th>
                      <th className="text-left px-4 py-2.5 font-medium">Status</th>
                      <th className="text-right px-4 py-2.5 font-medium"><span className="sr-only">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {usageRows.map((row: any, i: number) => {
                      // 'historical' is a day a PERSON entered. Marked, and
                      // marked QUIETLY — plain muted text in the Status cell,
                      // no fill, no border, no caps, no row tint. Matching the
                      // cert PDF and CSV, so the three surfaces a reader might
                      // compare cannot disagree about how loud this is.
                      //
                      // It is NOT the Voided treatment beside it, and that is
                      // the point. Voided is a WARNING — a day counted against
                      // a ticket that no longer exists, which somebody has to
                      // act on. This is PROVENANCE: the number is legitimate
                      // and this says where it came from. They must not
                      // compete, and as a matching badge they did.
                      const isHistorical = row.source === 'historical';
                      // The usage-row id IS the adjustment id for a historical
                      // row, so the ledger lookup needs no extra call. A row
                      // already superseded gets no Edit — one correction per
                      // entry is a UNIQUE index on the API side, and offering
                      // the button anyway buys a 409 the person cannot act on.
                      const entry = isHistorical ? adjustmentsById.get(row.id) : undefined;
                      const correctable = !!entry
                        && !entry.supersededById
                        && (entry.kind === 'opening_balance' || entry.kind === 'historical_day');
                      return (
                      <tr key={row.id ?? i} className="border-t border-fieldiq-border hover:bg-fieldiq-dark/50 transition-colors">
                        <td className="px-4 py-2.5 text-white whitespace-nowrap">
                          {row.ticketDeleted ? <s>{row.date}</s> : (row.date || '—')}
                        </td>
                        <td className="px-4 py-2.5 text-fieldiq-text whitespace-nowrap">{row.customerName || '—'}</td>
                        <td className="px-4 py-2.5 text-fieldiq-text whitespace-nowrap">{row.rigName || '—'}</td>
                        <td className="px-4 py-2.5 text-fieldiq-text whitespace-nowrap">{row.ticketNumber || '—'}</td>
                        <td className="px-4 py-2.5 text-fieldiq-text whitespace-nowrap">{row.jobNumber || '—'}</td>
                        <td className="px-4 py-2.5 text-fieldiq-text whitespace-nowrap">{row.wellName || '—'}</td>
                        {/* `?? '—'`, not `|| '—'`: a day count is a number and
                            0 is not absent. `||` would dash a legitimate 0. */}
                        <td className="px-4 py-2.5 text-fieldiq-text whitespace-nowrap">{row.daysUsed ?? '—'}</td>
                        <td className="px-4 py-2.5 whitespace-nowrap">
                          {row.ticketDeleted ? (
                            <span
                              className="inline-block rounded border border-red-500/50 bg-red-500/15 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-red-300"
                              title="The ticket behind this day was deleted. The day is still counted against the certification allowance, so the row is kept rather than hidden."
                            >
                              Voided
                            </span>
                          ) : isHistorical ? (
                            // No size class: it inherits the cell's, so the
                            // marker stays the same type as the row it
                            // annotates if the type scale ever changes.
                            // The kind (opening balance, correction, …) is
                            // named in the office ledger's own dialog, not
                            // here — the same two words the customer document
                            // prints, so the surfaces agree.
                            // The badge is the way THROUGH to the ledger, not
                            // just a label. This row's only other action is the
                            // pencil, and a correction cannot move an entry to
                            // a different unit — so on the one table where the
                            // wrong-unit case is visible, the badge is what
                            // takes the operator to where removal lives.
                            //
                            // Read-only roles keep the plain span: the ledger
                            // is not rendered for them at all, so a button
                            // would scroll to nothing.
                            certWritableUser && entry ? (
                              <Tooltip content="Entered by hand rather than counted from an approved ticket. It appears marked on the certification PDF and CSV too. Select it in the Certification Day Ledger below, where it can be removed from the count.">
                                <button
                                  type="button"
                                  onClick={() => focusInLedger([entry.id])}
                                  className="text-fieldiq-muted underline decoration-dotted underline-offset-2 hover:text-fieldiq-accent transition-colors"
                                >
                                  Manual entry
                                </button>
                              </Tooltip>
                            ) : (
                            <span
                              className="text-fieldiq-muted"
                              title="Entered by hand rather than counted from an approved ticket. It appears marked on the certification PDF and CSV too."
                            >
                              Manual entry
                            </span>
                            )
                          ) : null}
                        </td>
                        <td className="px-4 py-2.5 whitespace-nowrap text-right">
                          {certWritableUser && correctable && (
                            <Tooltip content="Correct this entry — records a new correcting row, the original is kept">
                              <button
                                type="button"
                                onClick={() => setCorrecting(entry!)}
                                className="text-fieldiq-muted hover:text-fieldiq-accent transition-colors"
                                aria-label={`Correct the ${certAdjustmentLabel(row.adjustmentKind).toLowerCase()} entry`}
                              >
                                <Pencil size={14} />
                              </button>
                            </Tooltip>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="py-8 px-4 text-center text-fieldiq-muted text-sm">
                No usage recorded.
                <p className="text-xs mt-2">
                  A day appears here once its ticket is office-approved, so
                  recent work can be absent — or it can be entered by hand from
                  a paper log, above.{' '}
                  {equipment.certStartDate
                    ? `Work before this certification cycle began (${fmtDate(equipment.certStartDate)}) counted against the previous cycle and is not listed.`
                    : 'Work counted against a previous certification cycle is not listed.'}
                </p>
              </div>
            )}
          </div>
        )}
        {/* The office LEDGER. Below the Usage Log rather than inside it: they
            answer different questions and are scoped differently — the Usage
            Log is this certificate, in the customer's vocabulary; this is
            every entry ever made, in the office's. Role-gated to match the
            API, which refuses the read outside {admin, office_staff,
            dispatcher}. */}
        {showSection('overview') && showCertTracking && certWritableUser && (
          <CertLedger
            equipmentId={equipment.id}
            adjustments={adjustments}
            reconciliation={reconciliation}
            certStartDate={equipment.certStartDate ?? null}
            canCorrect={certWritableUser}
            onCorrect={setCorrecting}
            focus={ledgerFocus}
          />
        )}

        {correcting && (
          <CertCorrectionDialog
            equipmentId={equipment.id}
            entry={correcting}
            onClose={() => setCorrecting(null)}
          />
        )}

        {/* Maintenance Schedules (preventive) */}
        {showSection('maintenance') && (
          <MaintenanceSchedules equipmentId={equipment.id} assetKind={assetKind} />
        )}

        {/* Maintenance History */}
        {showSection('maintenance') && (
        <div className="bg-fieldiq-card rounded-lg border border-fieldiq-border overflow-hidden">
          <div className="p-4 border-b border-fieldiq-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wrench size={18} className="text-fieldiq-accent" />
              <div>
                <h3 className="text-base font-bold text-white">Maintenance History</h3>
                <p className="text-xs text-fieldiq-text mt-0.5">
                  {maintenanceRecords.length} record{maintenanceRecords.length !== 1 ? 's' : ''}
                  {maintenanceRecords.length > 0 && (
                    <> · Total spend: <span data-testid="maintenance-total-spend" className="text-fieldiq-accent font-semibold">
                      {fmtCurrency(sumMoney(maintenanceRecords.map((r) => r.total_cost)), null, 0)}
                    </span></>
                  )}
                </p>
              </div>
            </div>
            <button
              onClick={() => { setEditingMaintenanceRecord(null); setShowMaintenanceDialog(true); }}
              className="flex items-center gap-1.5 bg-fieldiq-accent text-fieldiq-dark text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-amber-400 transition-colors"
            >
              <Plus size={14} /> Add Record
            </button>
          </div>

          {maintenanceRecords.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-fieldiq-dark text-fieldiq-muted text-xs uppercase">
                    <th className="text-left px-4 py-2.5 font-medium">Date</th>
                    <th className="text-left px-4 py-2.5 font-medium">Type</th>
                    <th className="text-left px-4 py-2.5 font-medium">Description</th>
                    <th className="text-right px-4 py-2.5 font-medium">Total Cost</th>
                    <th className="text-center px-4 py-2.5 font-medium">Status</th>
                    <th className="w-10 py-2.5 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {maintenanceRecords.map((rec) => {
                    const statusCfg: Record<string, { bg: string; text: string; label: string }> = {
                      scheduled: { bg: 'bg-blue-500/15', text: 'text-blue-400', label: 'Scheduled' },
                      in_progress: { bg: 'bg-amber-500/15', text: 'text-amber-400', label: 'In Progress' },
                      completed: { bg: 'bg-green-500/15', text: 'text-green-400', label: 'Completed' },
                      cancelled: { bg: 'bg-gray-500/15', text: 'text-gray-400', label: 'Cancelled' },
                    };
                    // An unrecognised status is not Completed. `status` is free text
                    // on the table (no CHECK), so anything can arrive, and the old
                    // fallback printed it as the most reassuring value in the map: a
                    // service nobody finished read as done. Unknown is a grey dash,
                    // as on the Equipment register (UNKNOWN_STATUS there).
                    const sc = (rec.status && Object.prototype.hasOwnProperty.call(statusCfg, rec.status))
                      ? statusCfg[rec.status]
                      : { bg: 'bg-gray-500/15', text: 'text-gray-400', label: '—' };
                    return (
                      <tr
                        key={rec.id}
                        className="border-t border-fieldiq-border hover:bg-fieldiq-dark/50 cursor-pointer transition-colors"
                        onClick={() => { setEditingMaintenanceRecord(rec); setShowMaintenanceDialog(true); }}
                      >
                        <td className="px-4 py-2.5 text-white whitespace-nowrap">
                          {fmtDate(rec.service_date)}
                        </td>
                        <td className="px-4 py-2.5 text-fieldiq-text whitespace-nowrap">{rec.maintenance_type || '—'}</td>
                        <td className="px-4 py-2.5 text-white">{rec.description || '—'}</td>
                        <td className="px-4 py-2.5 text-white text-right font-semibold whitespace-nowrap">
                          {fmtCurrency(rec.total_cost, null, 0)}
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span
                            data-testid="maintenance-status-badge"
                            className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${sc.bg} ${sc.text}`}
                          >
                            {sc.label}
                          </span>
                        </td>
                        <td className="py-2.5 px-2 text-center">
                          <Tooltip content="Download PDF">
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDownloadMaintenancePdf(rec.id, equipment?.unitNumber || equipment?.name); }}
                              disabled={downloadingRecordPdf === rec.id}
                              className="p-1.5 rounded hover:bg-fieldiq-dark text-fieldiq-muted hover:text-white transition-colors disabled:opacity-50"
                              aria-label="Download PDF"
                            >
                              {downloadingRecordPdf === rec.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                            </button>
                          </Tooltip>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="py-8 text-center text-fieldiq-muted text-sm">
              No maintenance records yet. Click "Add Record" to log maintenance.
            </div>
          )}
        </div>
        )}
      </main>

      {/* Maintenance Record Dialog */}
      <MaintenanceRecordDialog
        open={showMaintenanceDialog}
        onClose={() => { setShowMaintenanceDialog(false); setEditingMaintenanceRecord(null); }}
        preselectedEquipmentId={equipmentId}
        record={editingMaintenanceRecord}
        onSaved={() => refetchMaintenance()}
      />

      {/* Transfer Modal */}
      {showTransfer && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowTransfer(false)}>
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white mb-1">Transfer Equipment</h3>
            <p className="text-sm text-fieldiq-text mb-4">
              Move <span className="text-white font-medium">{displayName}</span> to a different location.
            </p>
            {/* Shop / Rig toggle */}
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => { setTransferType('shop'); setTransferRig(''); }}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${transferType === 'shop' ? 'bg-fieldiq-accent text-fieldiq-dark' : 'bg-fieldiq-dark border border-fieldiq-border text-fieldiq-text'}`}
              >
                Shop / Yard
              </button>
              <button
                type="button"
                onClick={() => { setTransferType('rig'); setTransferYardId(''); }}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${transferType === 'rig' ? 'bg-fieldiq-accent text-fieldiq-dark' : 'bg-fieldiq-dark border border-fieldiq-border text-fieldiq-text'}`}
              >
                Rig / Job Site
              </button>
            </div>
            <div className="mb-5">
              {transferType === 'shop' ? (
                <>
                  <label className={labelClass}>Destination Shop</label>
                  <select
                    className={inputClass}
                    value={transferYardId}
                    onChange={e => setTransferYardId(e.target.value)}
                  >
                    <option value="">Select shop...</option>
                    {yards.map((y: any) => (
                      <option key={y.id} value={y.id}>{y.name} — {y.city}</option>
                    ))}
                  </select>
                </>
              ) : (
                <>
                  <label className={labelClass}>Destination Rig / Job</label>
                  <select
                    className={inputClass}
                    value={transferRig}
                    onChange={e => setTransferRig(e.target.value)}
                  >
                    <option value="">Select rig...</option>
                    {activeJobs.map((j: any) => (
                      <option key={j.id} value={j.rig_name || j.rigName || j.job_number}>
                        {j.rig_name || j.rigName || 'No Rig'} — {j.job_number || j.jobNumber} ({j.customer_name || j.customerName || 'Unknown'})
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowTransfer(false)} className="flex-1 bg-fieldiq-dark border border-fieldiq-border text-white py-2.5 rounded-lg text-sm font-medium">Cancel</button>
              <button
                onClick={() => transferMutation.mutate(
                  transferType === 'rig'
                    ? { id: equipment.id, toRig: transferRig }
                    : { id: equipment.id, toYardId: transferYardId }
                )}
                disabled={(transferType === 'shop' ? !transferYardId : !transferRig) || transferMutation.isPending}
                className="flex-1 bg-fieldiq-accent text-fieldiq-dark py-2.5 rounded-lg text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {transferMutation.isPending ? 'Transferring...' : 'Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEdit && equipment && (
        <EditEquipmentModal
          equipment={equipment}
          onSave={(data, certCycleAction, certChangeReason) => editMutation.mutate({
            ...data,
            ...(certCycleAction ? { certCycleAction } : {}),
            ...(certChangeReason ? { certChangeReason } : {}),
          })}
          onClose={() => setShowEdit(false)}
          saving={editMutation.isPending}
        />
      )}

      {/* Photo Delete Confirmation */}
      {showPhotoDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-fieldiq-card rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="text-lg font-bold text-white mb-2">Remove Equipment Photo</h3>
            <p className="text-sm text-fieldiq-text mb-6">
              Remove the photo for "{equipment?.name}"?
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowPhotoDeleteConfirm(false)}
                className="flex-1 py-2.5 bg-fieldiq-dark text-white font-medium rounded-lg text-sm">
                Cancel
              </button>
              <button onClick={handlePhotoDelete}
                className="flex-1 py-2.5 bg-red-500 text-white font-semibold rounded-lg text-sm hover:bg-red-400">
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-fieldiq-card rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="text-lg font-bold text-white mb-2">Delete Equipment</h3>
            <p className="text-sm text-fieldiq-text mb-6">
              Are you sure you want to delete "{equipment?.name}"? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2.5 bg-fieldiq-dark text-white font-medium rounded-lg text-sm"
                disabled={deleteMutation.isPending}>
                Cancel
              </button>
              <button onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="flex-1 py-2.5 bg-red-500 text-white font-semibold rounded-lg text-sm hover:bg-red-400 disabled:opacity-50">
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function EditEquipmentModal({ equipment, onSave, onClose, saving }: {
  equipment: any;
  /**
   * @param certCycleAction present ONLY when the cert start date moved on a
   *   unit that already had a count — i.e. only when the user was actually
   *   asked. Absent otherwise, which the API reads as "no cycle decision to
   *   make" rather than as a stale default.
   */
  /**
   * @param certChangeReason present ONLY when the cert day counter moved to a
   *   number. The API refuses that move without one (400) since #620, because
   *   it is written as a `counter_set` ledger row and that kind requires a
   *   note. Absent on every other save, so an edit that never touched the
   *   count does not put a reason on the compliance record.
   */
  onSave: (data: Record<string, any>, certCycleAction?: 'carry' | 'reset', certChangeReason?: string) => void;
  onClose: () => void;
  saving: boolean;
}) {
  const { isMetric } = useLocale();
  const { user } = useAuth();
  // Who may TYPE OVER a certification counter or cycle start. The API rejects
  // everyone else with a 403 — see canWriteCertFields. The value itself stays
  // visible on the card behind this modal for every role.
  const certWritable = canWriteCertFields(user?.role);

  // ── Moving the cert start date is two different intentions ────────────────
  //
  // The unit went through inspection and the new cycle starts at nought
  // (RESET), or the date was simply wrong and the count means the same thing
  // either side of the correction (CARRY). The server cannot tell which from
  // the payload, and guessing `reset` ERASES a compliance count — so the
  // client says which, always, whenever the date actually moves on a unit that
  // has a count.
  //
  // The API defaults to `carry` with a logged warning when the field is absent,
  // for one release, so an older client keeps working. That default is a
  // migration aid, not a contract: this modal sends the field.
  const initialCertStart = calendarDayString(equipment.certStartDate) ?? String(equipment.certStartDate ?? '');
  const priorCounter: number | null = equipment.workingDaysSinceInspection ?? null;
  const [certCycleAction, setCertCycleAction] = useState<'carry' | 'reset'>('carry');

  // Pre-fill odometer / engine hours from the vehicle's existing readings
  // (current_meters comes from GET /equipment/:id). Odometer uses km when the
  // company is metric, mi otherwise; fall back to the legacy single-meter shape.
  const odometerKey = isMetric ? 'odometer_km' : 'odometer_mi';
  const cm: CurrentMeters | null = equipment.currentMeters ?? equipment.current_meters ?? null;
  const legacyMeter: CurrentMeter | null = equipment.currentMeter ?? equipment.current_meter ?? null;
  const odoReading = cm?.[odometerKey] ?? cm?.odometer_km ?? cm?.odometer_mi ?? null;
  const engReading = cm?.engine_hours ?? null;
  const initialOdometerVal = odoReading?.value
    ?? (legacyMeter?.type?.startsWith('odometer') && legacyMeter.value != null ? legacyMeter.value : null);
  const initialEngineHoursVal = engReading?.value
    ?? (legacyMeter?.type === 'engine_hours' && legacyMeter.value != null ? legacyMeter.value : null);
  const initialOdometer = initialOdometerVal != null ? String(initialOdometerVal) : '';
  const initialEngineHours = initialEngineHoursVal != null ? String(initialEngineHoursVal) : '';
  // Captured at open time so save can diff and skip appending an unchanged reading.
  const initialMeterRef = useRef({ odometer: initialOdometer, engineHours: initialEngineHours });

  const [form, setForm] = useState<any>({
    name: equipment.name || '',
    type: equipment.type || '',
    assetKind: equipment.assetKind ?? equipment.asset_kind ?? 'equipment',
    licensePlate: equipment.licensePlate ?? equipment.license_plate ?? '',
    vin: equipment.vin || '',
    serialNumber: equipment.serialNumber || '',
    manufacturer: equipment.manufacturer || '',
    size: equipment.size || '',
    capacity: equipment.capacity || '',
    status: equipment.status || 'available',
    notes: equipment.notes || '',
    certDayLimit: equipment.certDayLimit || undefined,
    certStartDate: equipment.certStartDate || '',
    certExpiryDate: equipment.certExpiryDate || '',
    certDescription: equipment.certDescription || '',
    workingDaysSinceInspection: equipment.workingDaysSinceInspection ?? '',
    odometer: initialOdometer,
    engineHours: initialEngineHours,
  });
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  // Calendar-day comparison on both sides. The stored value can arrive as
  // `2026-01-01T00:00:00.000Z` while the picker holds `2026-01-01`; a raw string
  // compare calls that a move, and would put the cycle question in front of
  // somebody who only edited the notes.
  const certStartMoved =
    (calendarDayString(form.certStartDate) ?? String(form.certStartDate ?? '')) !== initialCertStart;

  // ── A counter move has to say why (API 0388) ──────────────────────────────
  //
  // Since #620 the counter no longer lands in `equipment.working_days_since_
  // inspection` by a bare UPDATE. It goes through applyCertAdjustment as a
  // `counter_set` ledger row, and that kind carries `note NOT NULL` — so the
  // API answers 400 'Changing a certification day count requires a reason.
  // Send "certChangeReason".' when the number moves without one.
  //
  // #574 added this box to the AddEditModal on the equipment LIST screen and
  // not to this one, so from that deploy until now every counter edit started
  // from the equipment DETAIL page took the 400. Two modals write this column
  // and only one of them was taught the field. Keep them in step; the list
  // screen's copy is `AddEditModal` in src/screens/EquipmentScreen.tsx.
  const [certChangeReason, setCertChangeReason] = useState('');

  // `''` is the never-counted prefill (see the form initialiser) and
  // cleanFormPayload turns it back into `null` on the way out, so it has to
  // read as ABSENT here too. `Number('')` is 0, and treating that as a count
  // would demand a reason on a unit nobody has ever counted and whose payload
  // says `null` — asking for an explanation of an edit that is not happening.
  const rawCounter = form.workingDaysSinceInspection;
  const counterInForm: number | null =
    rawCounter === '' || rawCounter == null ? null : Number(rawCounter);
  // CHANGE, not presence. The modal prefills the box and PUTs the whole form
  // back, so gating on presence would ask for a reason on an edit that only
  // touched the notes — the same rule the API's own gate uses, and the same one
  // certStartMoved above uses for the cycle question.
  //
  // `counterInForm !== null` because clearing the box to null is the "not
  // tracked" statement, which stays on the old audited path and writes no
  // ledger row: the ledger holds integers and cannot express absence.
  const counterMoved = counterInForm !== null && counterInForm !== priorCounter;
  const counterReasonMissing = counterMoved && certChangeReason.trim() === '';

  // Which cert level the user PICKED, as opposed to which one the day limit
  // happens to look like. Custom cannot be derived — a custom limit of exactly
  // 250 is indistinguishable from Level 3 — so clicking Custom had nothing to
  // write and did nothing at all. This is that missing state. The modal mounts
  // fresh per open, so it resets with the form.
  // Mirrors AddEditModal in src/screens/EquipmentScreen.tsx; keep the two in step.
  const [certLevelOverride, setCertLevelOverride] = useState<'custom' | null>(null);

  const certTrackingEnabled = !!form.certDayLimit || !!form.certDescription || !!form.certStartDate;

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl w-full max-w-md max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-fieldiq-border flex-shrink-0">
          <h2 className="text-lg font-bold text-white">Edit Equipment</h2>
          <button onClick={onClose} className="text-fieldiq-muted hover:text-white"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          <div>
            <label className={labelClass}>Asset type</label>
            <div className="flex gap-2">
              {(['equipment', 'vehicle'] as const).map(kind => (
                <button
                  key={kind}
                  type="button"
                  onClick={() => set('assetKind', kind)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    form.assetKind === kind
                      ? 'bg-fieldiq-accent text-fieldiq-dark border-fieldiq-accent'
                      : 'bg-fieldiq-dark text-fieldiq-text border-fieldiq-border hover:border-fieldiq-accent'
                  }`}
                >
                  {kind === 'equipment' ? 'Equipment' : 'Vehicle'}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className={labelClass}>Name</label>
            <input className={inputClass} value={form.name} onChange={e => set('name', e.target.value)} />
          </div>
          {form.assetKind === 'vehicle' && (
            <>
              <div>
                <label className={labelClass}>License plate</label>
                <input className={inputClass} placeholder="e.g. ABC-1234" value={form.licensePlate} onChange={e => set('licensePlate', e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>VIN</label>
                <input className={inputClass} placeholder="17-character VIN" maxLength={17} value={form.vin} onChange={e => set('vin', e.target.value.toUpperCase())} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Odometer ({isMetric ? 'km' : 'mi'})</label>
                  <input className={inputClass} type="number" min={0} placeholder="e.g. 125000" value={form.odometer ?? ''} onChange={e => set('odometer', e.target.value)} />
                </div>
                <div>
                  <label className={labelClass}>Engine hours</label>
                  <input className={inputClass} type="number" min={0} placeholder="e.g. 4500" value={form.engineHours ?? ''} onChange={e => set('engineHours', e.target.value)} />
                </div>
              </div>
            </>
          )}
          <div>
            <label className={labelClass}>Type</label>
            <input className={inputClass} value={form.type} onChange={e => set('type', e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Serial Number</label>
            <input className={inputClass} value={form.serialNumber} onChange={e => set('serialNumber', e.target.value)} />
          </div>
          <div>
            <label className={labelClass}>Manufacturer</label>
            <input className={inputClass} value={form.manufacturer} onChange={e => set('manufacturer', e.target.value)} />
          </div>
          {form.assetKind !== 'vehicle' && (
            <>
              <div>
                <label className={labelClass}>Size</label>
                <input className={inputClass} placeholder="e.g. 13-3/8&quot;, 4-1/2&quot;" value={form.size} onChange={e => set('size', e.target.value)} />
              </div>
              <div>
                <label className={labelClass}>Capacity</label>
                <input className={inputClass} placeholder="e.g. 50,000 ft/lbs, 150T" value={form.capacity} onChange={e => set('capacity', e.target.value)} />
              </div>
            </>
          )}
          <div>
            <label className={labelClass}>Status</label>
            <select className={inputClass} value={form.status} onChange={e => set('status', e.target.value)}>
              <option value="available">Available</option>
              <option value="reserved">Reserved</option>
              <option value="in_use">In Use</option>
              <option value="left_on_site">On Location</option>
              <option value="maintenance">In Maintenance</option>
              <option value="retired">Retired</option>
            </select>
          </div>

          {form.assetKind !== 'vehicle' && (
            <div className="bg-fieldiq-dark rounded-lg p-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={certTrackingEnabled}
                  onChange={e => {
                    if (!e.target.checked) {
                      set('certDayLimit', undefined); set('certStartDate', ''); set('certExpiryDate', ''); set('certDescription', '');
                    } else {
                      set('certDayLimit', 500); set('certDescription', 'CAODC Level 4');
                    }
                  }}
                  className="w-5 h-5 rounded border-fieldiq-border text-fieldiq-accent focus:ring-fieldiq-accent bg-fieldiq-card"
                />
                <div>
                  <span className="text-sm font-medium text-white">Inspections</span>
                  <p className="text-xs text-fieldiq-muted">Working day limit and inspection certificate</p>
                </div>
              </label>
              {certTrackingEnabled && (
                <div className="mt-4 space-y-3 border-t border-fieldiq-border pt-4">
                  <div>
                    <label className={labelClass}>Certification Level</label>
                    <div className="grid grid-cols-3 gap-2">
                      {CERT_LEVEL_PRESETS.map(preset => {
                        const isActive = preset.days
                          ? certLevelOverride !== 'custom' && form.certDayLimit === preset.days
                          : certLevelOverride === 'custom' || !isPresetDayLimit(form.certDayLimit);
                        return (
                          <button key={preset.label} type="button"
                            onClick={() => {
                              if (preset.days) {
                                setCertLevelOverride(null);
                                set('certDayLimit', preset.days);
                                set('certDescription', preset.label);
                              } else {
                                // Custom: keep the current day limit — the
                                // Working Day Limit field below is editable and
                                // the user types their own number — but stamp
                                // the description, or the record keeps whatever
                                // level was last selected and every read-only
                                // screen prints that stale level forever.
                                setCertLevelOverride('custom');
                                set('certDescription', preset.label);
                              }
                            }}
                            className={`py-2 rounded-lg text-xs font-medium transition ${isActive ? 'bg-fieldiq-accent text-fieldiq-dark' : 'bg-fieldiq-card text-fieldiq-text'}`}>
                            {preset.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Working Day Limit</label>
                    {/* A cleared field is "not tracked", not a limit of 0. Number('') is 0,
                        and 0 used to reach the API, which stored it — three live vehicles
                        carried cert_day_limit = 0 that way (API #498). Absent is not zero. */}
                    <input className={inputClass} type="number" min={1} placeholder="500" value={form.certDayLimit || ''} onChange={e => set('certDayLimit', e.target.value === '' ? undefined : Number(e.target.value))} />
                  </div>
                  {/* Cert EXPIRY is not gated — only the start date is, because
                      moving it resets which usage rows count. When the start
                      date is hidden the two-column row would leave expiry
                      stranded in half a grid, so the wrapper drops to a plain
                      block and expiry reads as an ordinary full-width field. */}
                  <div className={certWritable ? 'grid grid-cols-2 gap-3' : undefined}>
                    {certWritable && (
                      <div>
                        <label className={labelClass}>Cert Start Date</label>
                        <DatePicker value={form.certStartDate || ''} onChange={v => set('certStartDate', v)} />
                      </div>
                    )}
                    <div>
                      <label className={labelClass}>Cert Expiry Date</label>
                      <DatePicker value={form.certExpiryDate || ''} onChange={v => set('certExpiryDate', v)} />
                    </div>
                  </div>
                  {/* Shown only when the date ACTUALLY MOVES on a unit that has
                      a count — the two conditions that make the question mean
                      anything. `!== null` rather than a falsy check: a counter
                      of 0 is a real count ("counted, nought used"), and a unit
                      sitting at 0 still has to be asked, because carrying 0 and
                      resetting to 0 are the same number arrived at two ways and
                      only one of them writes a cycle_reset row. */}
                  {certWritable && certStartMoved && priorCounter !== null && (
                    <div className="bg-fieldiq-dark rounded-lg border border-amber-500/40 p-3">
                      <p className="text-xs text-white font-medium">
                        This tool has {priorCounter} certification day{priorCounter === 1 ? '' : 's'} counted.
                        What should happen to that count?
                      </p>
                      <div className="mt-2 space-y-2">
                        {([
                          ['carry', 'Keep the count', 'The start date was wrong and is being corrected. The count means the same thing.'],
                          ['reset', 'Start the new cycle at zero', 'The tool went through inspection. Days worked under the old certificate stop counting.'],
                        ] as const).map(([value, title, detail]) => (
                          <label key={value} className="flex items-start gap-2.5 cursor-pointer">
                            <input
                              type="radio" name="certCycleAction" value={value}
                              checked={certCycleAction === value}
                              onChange={() => setCertCycleAction(value)}
                              className="mt-0.5 accent-amber-500"
                            />
                            <span>
                              <span className="block text-sm text-white">{title}</span>
                              <span className="block text-xs text-fieldiq-muted">{detail}</span>
                            </span>
                          </label>
                        ))}
                      </div>
                      {certCycleAction === 'reset' && (
                        <p className="mt-2 text-xs text-amber-300">
                          A reset is recorded in the certification log as an entry of
                          −{priorCounter} day{priorCounter === 1 ? '' : 's'}. Nothing is deleted.
                        </p>
                      )}
                    </div>
                  )}
                  {certWritable && (
                    <div>
                      <label className={labelClass}>Working Days Since Last Inspection</label>
                      <input className={inputClass} type="number" min={0} placeholder="0" value={form.workingDaysSinceInspection} onChange={e => set('workingDaysSinceInspection', e.target.value ? Number(e.target.value) : null)} />
                      {/* Shown only once the number has actually MOVED — see
                          counterMoved above. Mirrors the same block in
                          AddEditModal on the equipment list screen; the two
                          modals write one column and must ask the same
                          question. */}
                      {counterMoved && (
                        <div className="mt-3 bg-fieldiq-dark rounded-lg border border-amber-500/40 p-3">
                          <label className={labelClass} htmlFor="cert-change-reason-detail">
                            Why is the count changing? <span className="text-amber-400">Required</span>
                          </label>
                          <input
                            id="cert-change-reason-detail" className={inputClass}
                            placeholder="e.g. Recount against log book 3"
                            value={certChangeReason}
                            onChange={e => setCertChangeReason(e.target.value)}
                          />
                          <p className="text-xs text-fieldiq-muted mt-1.5">
                            {priorCounter === null
                              ? `Setting this count to ${counterInForm}.`
                              : `Changing this count from ${priorCounter} to ${counterInForm}.`}
                            {' '}It is recorded in the certification log with your name and cannot be edited afterwards.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div>
            <label className={labelClass}>Notes</label>
            <textarea className={inputClass} rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>

        <div className="p-5 border-t border-fieldiq-border flex-shrink-0">
          <button
            onClick={() => {
              // Defence in depth: Save is `disabled` under exactly this
              // condition, so this is unreachable through the UI. It is here so
              // a programmatic submit cannot skip the explanation and collect a
              // 400 — the same belt AddEditModal's handleSubmit carries.
              if (counterReasonMissing) return;
              // Only append a new meter reading if the value actually changed
              // from what was pre-filled at open time — avoids duplicate readings.
              const odometerChanged = form.odometer !== '' && form.odometer !== initialMeterRef.current.odometer;
              const hoursChanged = form.engineHours !== '' && form.engineHours !== initialMeterRef.current.engineHours;
              onSave(cleanFormPayload({
              name: form.name,
              type: form.type,
              asset_kind: form.assetKind,
              license_plate: form.assetKind === 'vehicle' ? (form.licensePlate || null) : null,
              vin: form.assetKind === 'vehicle' ? (form.vin || null) : null,
              serialNumber: form.serialNumber || null,
              manufacturer: form.manufacturer || null,
              size: form.assetKind === 'vehicle' ? null : (form.size || null),
              capacity: form.assetKind === 'vehicle' ? null : (form.capacity || null),
              status: form.status,
              notes: form.notes || null,
              // `|| null`, not the bare value: a 0 must never leave this form as a
              // budget. The API now refuses to store one (0359's CHECK) and maps 0
              // to NULL itself; this is the same rule stated where the value is born.
              certDayLimit: form.assetKind === 'vehicle' ? null : (form.certDayLimit || null),
              certStartDate: form.assetKind === 'vehicle' ? null : form.certStartDate,
              certExpiryDate: form.assetKind === 'vehicle' ? null : form.certExpiryDate,
              certDescription: form.assetKind === 'vehicle' ? null : (form.certDescription || null),
              workingDaysSinceInspection: form.assetKind === 'vehicle' ? null : form.workingDaysSinceInspection,
              // BOTH inspection-date columns are deliberately ABSENT, not null
              // — `lastInspectionDate` (#650) and `nextInspectionDate` (this
              // change). No field on this form edits either, so sending them
              // could only echo back the value the row held when the page
              // loaded — and worse, `cleanFormPayload` turned the `''` prefill
              // into an explicit NULL, so a record the API had not yet dated
              // wrote NULL on EVERY save, with no stale-load timing needed. The
              // form-state prefills are gone too, so there is nothing left to
              // echo. `sanitizeUpdate` writes any key that is PRESENT; absence
              // is the only way to say "I am not speaking about this column".
              odometer: form.assetKind === 'vehicle' && odometerChanged ? Number(form.odometer) : undefined,
              engine_hours: form.assetKind === 'vehicle' && hoursChanged ? Number(form.engineHours) : undefined,
            }, {
              numericFields: ['certDayLimit', 'workingDaysSinceInspection'],
              dateFields: ['certStartDate', 'certExpiryDate'],
            }),
            // Sent only when the question was actually put to the user. Sending
            // it on every save would tell the API a decision was made where
            // none was, and would take a straggler-detecting warning off the
            // one path that still needs it.
            certStartMoved && priorCounter !== null ? certCycleAction : undefined,
            // Sent only when the counter actually moved to a number. Sending it
            // on every save would put a reason on the record for an edit that
            // never touched the count.
            counterMoved ? certChangeReason.trim() : undefined);
            }}
            disabled={!form.name || !form.type || saving || counterReasonMissing}
            className="w-full bg-fieldiq-accent text-fieldiq-dark py-3 rounded-xl font-semibold text-sm hover:bg-amber-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
